import { store } from '@/store';

/**
 * Currency symbols map for common currencies
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  BDT: '৳',
  INR: '₹',
  JPY: '¥',
  CNY: '¥',
  AUD: 'A$',
  CAD: 'C$',
};

/**
 * Format price with the platform's currency symbol
 * @param price - The numeric price value
 * @param includeDecimals - Whether to include decimal places (default: true)
 * @returns Formatted price string with currency symbol
 */
export const formatPrice = (price: number | string, includeDecimals: boolean = true): string => {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return '0.00';
  }

  // Get currency settings from Redux store
  const state = store.getState();
  const currencyCode = state.platformSettings?.settings?.currency_code || 'USD';
  const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || state.platformSettings?.settings?.currency_symbol || '$';

  // Format with or without decimals
  const formattedNumber = includeDecimals
    ? numericPrice.toFixed(2)
    : Math.round(numericPrice).toString();

  // Add thousands separators
  const parts = formattedNumber.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const finalPrice = parts.join('.');

  // For BDT, INR, symbol comes after the number; for most others, before
  if (currencyCode === 'BDT' || currencyCode === 'INR') {
    return `${finalPrice} ${currencySymbol}`;
  }

  return `${currencySymbol}${finalPrice}`;
};

/**
 * Get the current currency symbol from the platform settings
 * @returns Current currency symbol
 */
export const getCurrencySymbol = (): string => {
  const state = store.getState();
  const currencyCode = state.platformSettings?.settings?.currency_code || 'USD';
  return CURRENCY_SYMBOLS[currencyCode] || state.platformSettings?.settings?.currency_symbol || '$';
};

/**
 * Get the current currency code from the platform settings
 * @returns Current currency code (e.g., 'USD', 'EUR')
 */
export const getCurrencyCode = (): string => {
  const state = store.getState();
  return state.platformSettings?.settings?.currency_code || 'USD';
};

/**
 * Get the current currency name from the platform settings
 * @returns Current currency name (e.g., 'US Dollar', 'Euro')
 */
export const getCurrencyName = (): string => {
  const state = store.getState();
  return state.platformSettings?.settings?.currency_name || 'US Dollar';
};
